import { useCallback } from "react";
import { toast } from "sonner";
import { useAppDispatch } from "redux/hooks";
import { triggerOrderRefresh } from "redux/features/CreateGICOrder";
import { applyandRemoveCouponOrder, ApplyCouponOrder } from "pages/GenericOrder/apis";

export const useDiscountOperations = (genericOrderId: number) => {
  const dispatch = useAppDispatch();

  const getAuthToken = useCallback(() => {
    const auth = localStorage.getItem("authToken");
    return auth ? JSON.parse(auth) : null;
  }, []);

  const applyFlatDiscount = useCallback(async (discountAmount: number): Promise<boolean> => {
    try {
      const payload = {
        generic_order_details_id: Number(genericOrderId),
        discount_coupon_id: [],
        amount: discountAmount,
        using_code: false,
      };

      const res = await applyandRemoveCouponOrder(payload);
      
      if (res) {
        dispatch(triggerOrderRefresh(true));
        toast.success(`Flat discount of ₹${discountAmount} applied successfully`);
        return true;
      }
      
      toast.error("Failed to apply flat discount");
      return false;
    } catch (err: any) {
      const message = err?.response?.data?.payload ||
                     err?.response?.data?.message ||
                     "Failed to apply flat discount";
      toast.error(message);
      return false;
    }
  }, [genericOrderId, dispatch]);

  const applyCouponDiscount = useCallback(async (couponId: number, code: string, discountAmount?: number): Promise<boolean> => {
    try {
      const payload = {
        generic_order_details_id: Number(genericOrderId),
        discount_coupon_id: [Number(couponId)],
        amount: discountAmount,
        using_code: true,
      };

      const res = await applyandRemoveCouponOrder(payload);
      
      if (res) {
        dispatch(triggerOrderRefresh(true));
        toast.success("Coupon applied successfully");
        return true;
      }
      
      toast.error("Failed to apply coupon");
      return false;
    } catch (err: any) {
      const message = err?.response?.data?.payload ||
                     err?.response?.data?.message ||
                     "Failed to apply coupon";
      toast.error(message);
      return false;
    }
  }, [genericOrderId, dispatch]);

  const removeDiscount = useCallback(async (): Promise<boolean> => {
    const token = getAuthToken();
    if (!token) return false;

    try {
      const requestBody = {
        generic_order_details_id: Number(genericOrderId),
        discount_coupon_id: [],
        flat_discount_amount: 0,
      };

      const payload = {
        data: JSON.stringify(requestBody),
        headers: { Authorization: `Bearer ${token}` },
      };

      await ApplyCouponOrder(payload);
      dispatch(triggerOrderRefresh(true));
      toast.success("Discount removed successfully");
      return true;
    } catch {
      toast.error("Failed to remove discount");
      return false;
    }
  }, [genericOrderId, dispatch, getAuthToken]);

  return { applyFlatDiscount, applyCouponDiscount, removeDiscount };
};